import useRequest from "../../hooks/useRequest";
import { BringzzImage, BringzzText, BringzzPostingCard, BringzzButton, BringzzIcon } from "@bringzz/components";
import AvatarExample from "../../assets/images/AvatarExample.png";
import React, { useState, useEffect, useRef } from "react";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigation } from 'context/NavigationContext';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'context/AuthContext';
import { usePosting } from 'context/PostingContext';
import { useBookmark } from 'context/BookmarkContext';
import LetterAvatar from '../../pages/Pharmacy/Profile/components/LetterAvatar';
import BookmarkCollectionDrawer from '../BookmarkCollectionDrawer';

const PostingCardElement = ({ post, user, preview, collections = [], pharmacyName, bookmarkedItems = new Set() }) => {
  const { t } = useTranslation('postingCard');
  const { sendRequest, loading } = useRequest();
  const { user: authUser } = useAuth();
  const { getLikeData, fetchLikeData, toggleLike, getComments, fetchComments } = usePosting();
  const { getBookmarkStatus, toggleBookmark, collections: bookmarkCollections, createCollection, bookmarks, bookmarksLoaded, removeBookmark } = useBookmark();
  const modalRef = useRef(null);

  const [showBookmarkDrawer, setShowBookmarkDrawer] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [showCommentsDrawer, setShowCommentsDrawer] = useState(false);
  const [editingComment, setEditingComment] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [localBookmarkStatus, setLocalBookmarkStatus] = useState(null);
  const currentUserId = user.id;
  // Like data now comes from AuthContext
  const { navigate } = useNavigation();

  // Use local bookmark status if available, otherwise use post data or context
  const bookmarkStatus = React.useMemo(() => {
    if (localBookmarkStatus !== null) {
      return localBookmarkStatus;
    }
    if (post?.isBookmarked !== undefined) {
      return post.isBookmarked ? 'success' : 'error';
    }
    // Fallback to context for backward compatibility
    if (!bookmarksLoaded) return 'error';
    return getBookmarkStatus(post?.integrationId, 0);
  }, [localBookmarkStatus, post?.isBookmarked, getBookmarkStatus, post?.integrationId, bookmarksLoaded]);

useEffect(() => {
  if (post?.integrationId) {
    fetchLikeData(post.integrationId);
  }
}, [post?.integrationId, fetchLikeData]);

const handleToggleLike = async () => {
  await toggleLike(post.integrationId);
};

const handleCopyPostUrl = async () => {
  try {
    const postUrl = `${window.location.origin}/post/details/${post.integrationId}`;
    await navigator.clipboard.writeText(postUrl);
    toast.success(t('success.copyPostUrl'));
    setTimeout(() => {
      navigate(`/post/details/${post.integrationId}`);
    }, 3000);
  } catch (error) {
    console.error('Failed to copy post URL:', error);
    toast.error(t('errors.copyPostUrl'));
  }
};

  const addBookmarkToCollection = (collectionUuid, newBookmark) => {
    return bookmarkCollections.map(collection => {
      if (collection.integrationId === collectionUuid) {
        return {
          ...collection,
          bookmarks: [...(collection.bookmarks || []), newBookmark]
        };
      }
      return collection;
    });
  }

  const removeBookmarkToCollection = (bookmarkUuid) => {
    return bookmarkCollections.map(collection => {
      return {
        ...collection,
        bookmarks: (collection.bookmarks || []).filter(
          bookmark => bookmark.integrationId !== bookmarkUuid
        )
      };
    });
  }

  // Collections are now handled by BookmarkContext

  const addBookmarkToCollectionNew = async (collectionUuid = null) => {
    const success = await toggleBookmark(post.integrationId, 0, collectionUuid);
    if (success) {
      setLocalBookmarkStatus('success');
      setShowBookmarkDrawer(false);
      const bookmarkUrl = collectionUuid ? `/settings/bookmarks/collection?id=${collectionUuid}&tab=posts` : '/settings/bookmarks';
      toast.success(
        <div>
          {t('component.bookmarks.post_bookmarked')}{' '}
          <span 
            onClick={() => navigate(bookmarkUrl)}
            className="underline cursor-pointer text-blue-600 hover:text-blue-800"
          >
            View bookmarks
          </span>
        </div>
      );
    } else {
      toast.error(t('component.bookmarks.bookmark_error'));
    }
  };

  const handleRemoveBookmark = async () => {
    const success = await removeBookmark(post.integrationId, 0);
    if (success) {
      setLocalBookmarkStatus('error');
      toast.success(t('component.bookmarks.post_bookmark_removed'));
    } else {
      toast.error(t('component.bookmarks.bookmark_error'));
    }
  };

  const addBookmark = async (collectionUuid = null) => {
    if (!authUser) {
      navigate('/login');
      return Promise.reject(new Error('Not authenticated'));
    }
    
    if (bookmarkStatus === 'success') {
      await handleRemoveBookmark();
      return Promise.resolve();
    } else {
      setShowBookmarkDrawer(true);
      return Promise.resolve();
    }
  };

  // Bookmark deletion is now handled by BookmarkContext

  // Collection creation is now handled by BookmarkContext

  const handleCreateComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      const response = await sendRequest({
        method: 'POST',
        url: '/module/comment/createComment',
        data: {
          content: newComment,
          title: 'Comment',
          postingUuid: post.integrationId
        }
      });
      
      if (response.status === 'success') {
        setNewComment('');
        fetchComments(post.integrationId);
      }
    } catch (error) {
      console.error('Error creating comment:', error);
    }
  };

  const handleEditComment = (comment) => {
    setEditingComment(comment);
    setEditContent(comment.content);
    setActiveDropdown(null);
  };

  const handleUpdateComment = async () => {
    if (!editContent.trim() || !editingComment) return;
    
    try {
      const response = await sendRequest({
        method: 'POST',
        url: '/module/comment/updateComment',
        data: {
          comment_uuid: editingComment.commentUuid,
          content: editContent
        }
      });
      
      if (response.status === 'success') {
        setEditingComment(null);
        setEditContent('');
        fetchComments(post.integrationId);
      }
    } catch (error) {
      console.error('Error updating comment:', error);
    }
  };

  const handleDeleteComment = async (commentUuid) => {
    try {
      const response = await sendRequest({
        method: 'POST',
        url: '/module/comment/deleteComment',
        data: {
          comment_uuid: commentUuid
        }
      });
      
      if (response.status === 'success') {
        fetchComments(post.integrationId);
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const toggleCommentsDrawer = () => {
    if (!showCommentsDrawer) {
      fetchComments(post.integrationId);
    }
    setShowCommentsDrawer(!showCommentsDrawer);
  };

  const toggleDropdown = (commentId) => {
    setActiveDropdown(activeDropdown === commentId ? null : commentId);
  };

  const renderCommentsDrawer = () => {
    if (!showCommentsDrawer) return null;

    return (
      <div className="bg-gray-50 rounded-lg p-4 mt-2">
        <div className="mb-4">
          <BringzzText tag="h4" className="font-semibold">{t('comments.title')}</BringzzText>
        </div>
        
        <div className="space-y-3">
            {getComments(post.integrationId).length === 0 ? (
              <div className="text-center py-8">
                <BringzzText className="text-gray-500">{t('comments.noComments')}</BringzzText>
              </div>
            ) : (
              getComments(post.integrationId).map(comment => {
                const isEdited = comment.created !== comment.updated;

                // Format date in German/Austrian format (DD.MM.YYYY HH:MM, 24-hour)
                const formatDateGerman = (dateStr) => {
                  if (!dateStr) return '';
                  // If already in DD.MM.YYYY HH:mm format, return as is
                  if (/^\d{2}\.\d{2}\.\d{4} \d{2}:\d{2}$/.test(dateStr)) {
                    return dateStr;
                  }
                  // Parse ISO or other date formats and convert to German format
                  const date = new Date(dateStr);
                  if (isNaN(date.getTime())) return dateStr;
                  const day = String(date.getDate()).padStart(2, '0');
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const year = date.getFullYear();
                  const hours = String(date.getHours()).padStart(2, '0');
                  const minutes = String(date.getMinutes()).padStart(2, '0');
                  return `${day}.${month}.${year} ${hours}:${minutes}`;
                };

                const dateText = isEdited
                  ? t('comments.edited', { date: formatDateGerman(comment.updated) })
                  : formatDateGerman(comment.created);
                
                const isCurrentUserComment = comment.createdById == currentUserId;
                
                return (
                  <div key={comment.id} className="bg-white rounded-lg p-3">
                    <div className="flex gap-3">
                      <img 
                        src={comment.user_pic || AvatarExample} 
                        className="w-10 h-10 rounded-full object-cover" 
                        alt="User"
                      />
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <div>
                            <BringzzText tag="h5" className="font-semibold text-sm">
                              {comment.user_first_name} {comment.user_last_name}
                            </BringzzText>
                            <BringzzText className="text-gray-500 text-xs">
                              {dateText}
                            </BringzzText>
                          </div>
                          {isCurrentUserComment && (
                            <div className="relative">
                                <BringzzButton
                                    variant="ghost"
                                    size="sm"
                                    className="text-black-800 hover:text-gray-800 p-2"
                                    onClick={() => toggleDropdown(comment.id)}
                                    >
                                    ...
                                    </BringzzButton>
                                {activeDropdown === comment.id && (
                                <div className="absolute right-0 mt-1 w-32 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                                    <div 
                                    className="flex items-center w-full text-left px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer"
                                    onClick={() => handleEditComment(comment)}
                                    >
                                    <BringzzIcon 
                                        icon="IconPencil" 
                                        folder="LineIcons"
                                        size="14"
                                        className="mr-2 text-gray-500"
                                    />
                                    {t('comments.edit')}
                                    </div>
                                    <div 
                                    className="flex items-center w-full text-left px-3 py-2 text-sm text-red-500 hover:bg-gray-50 cursor-pointer"
                                    onClick={() => handleDeleteComment(comment.commentUuid)}
                                    >
                                    <BringzzIcon 
                                        icon="IconTrashCan" 
                                        folder="LineIcons"
                                        size="14"
                                        className="mr-2"
                                    />
                                    {t('comments.delete')}
                                    </div>
                                </div>
                                )}
                            </div>
                            )}
                        </div>
                        
                        {editingComment?.id === comment.id ? (
                          <div className="mt-2">
                            <textarea
                              value={editContent}
                              onChange={(e) => {
                                setEditContent(e.target.value);
                                e.target.style.height = 'auto';
                                e.target.style.height = e.target.scrollHeight + 'px';
                              }}
                              className="w-full mb-2 p-2 border border-gray-300 rounded-lg focus:ring-0 focus:outline-none focus:border-gray-400 resize-none overflow-hidden text-sm"
                              rows={2}
                              style={{ minHeight: '48px', maxHeight: '150px' }}
                            />
                            <div className="flex justify-end gap-2">
                              <BringzzButton
                                size="sm"
                                variant="outline"
                                onClick={() => setEditingComment(null)}
                              >
                                {t('comments.cancel')}
                              </BringzzButton>
                              <BringzzButton
                                size="sm"
                                onClick={handleUpdateComment}
                                disabled={loading || !editContent.trim()}
                              >
                                {t('comments.save')}
                              </BringzzButton>
                            </div>
                          </div>
                        ) : (
                          <BringzzText className="mt-1 text-sm break-words overflow-hidden" style={{ overflowWrap: 'anywhere' }}>
                            {comment.content}
                          </BringzzText>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
        </div>
        
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center gap-2">
            <img 
              src={user.userPic || AvatarExample} 
              className="w-10 h-10 rounded-full object-cover" 
              alt="User"
            />
            <div className="flex-1 flex items-center gap-2 bg-white rounded-2xl pl-4 pr-2 py-2">
              <textarea
                placeholder={t('comments.placeholder')}
                value={newComment}
                onChange={(e) => {
                  setNewComment(e.target.value);
                  e.target.style.height = 'auto';
                  e.target.style.height = e.target.scrollHeight + 'px';
                }}
                className="flex-1 bg-transparent border-none focus:ring-0 focus:outline-none resize-none overflow-hidden text-sm"
                rows={1}
                style={{ minHeight: '24px', maxHeight: '120px' }}
              />
              {newComment.trim() && (
                <BringzzIcon
                  icon="IconSend"
                  folder="LineIcons"
                  size="24"
                  className={`transition-colors ${newComment.length >= 1000 ? 'text-gray-300 cursor-not-allowed' : 'text-blue-500 hover:text-blue-600 cursor-pointer'}`}
                  onClick={newComment.length >= 1000 ? undefined : handleCreateComment}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className='p-4 space-y-4'>
      {/* Type 1: Article Post - Displays an article with headline, description, and content blocks */}
      {post && post.type === 1 &&
        <>
        <BringzzPostingCard
          uuid={post.integrationId || null}
          avatar={preview ? (user?.userPic || AvatarExample) : (post.user_pic || AvatarExample)}
          name={preview ? (user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.email || 'User') : `${post.user_first_name || ''} ${post.user_last_name || ''}`.trim()}
          isPreview={preview}
          header={
            <BringzzImage
              className='w-full h-48 object-cover'
              src={post.ogImage ? post.ogImage : post.images[0].content}
              alt='post image'
            ></BringzzImage>
          }
          title={post.headline || ''}
          description={post.description || ''}
          action={{
            label: t('actions.readMore'),
            icon: 'IconEbook',
            iconFolder: 'LineIcons',
            onClick: () => navigate(`/post/details/${post.integrationId}`)
          }}
          addBookmark={addBookmark}
          hasBookmark={bookmarkStatus === 'success'}
          deleteBookmark={handleRemoveBookmark}
          collections={bookmarkCollections}
          createCollection={createCollection}
          onCommentClick={toggleCommentsDrawer}
          commentsCount={getComments(post.integrationId).length}
          likesCount={getLikeData(post.integrationId).count}
          isLiked={getLikeData(post.integrationId).isLiked}
          onLikeClick={handleToggleLike}
          onCopyPostUrl={handleCopyPostUrl}
        >
          {post.blocks && post.blocks.map((block, index) => {
            return <div key={index} className='px-3 py-2'>
              {block.component == "body_copy" && <BringzzText tag='p'>{block.content}</BringzzText>}
              {block.component == "inline_heading" &&
                <BringzzText tag='h3' className='font-semibold'>{block.content}</BringzzText>}
            </div>
          })}
        </BringzzPostingCard>
        {renderCommentsDrawer()}
        </>
      }

      {/* Type 2: Standard Post - Basic post with image, headline, and description */}
      {post && post.type === 2 &&
        <>
        <BringzzPostingCard
          uuid={post.integrationId || null}
          avatar={preview ? (user?.userPic || AvatarExample) : (post.user_pic || AvatarExample)}
          name={preview ? (user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.email || 'User') : `${post.user_first_name || ''} ${post.user_last_name || ''}`.trim()}
          isPreview={preview}
          header={
            <BringzzImage
              className='w-full h-48 object-cover'
              src={post.ogImage ? post.ogImage : post.images[0].content}
              alt='post image'
            ></BringzzImage>
          }
          title={post.headline || ''}
          description={post.description || ''}
          action={{
            label: 'Read more',
            icon: 'IconEbook',
            iconFolder: 'LineIcons',
            onClick: () => navigate(`/post/details/${post.integrationId}`)
          }}
          addBookmark={addBookmark}
          hasBookmark={bookmarkStatus === 'success'}
          deleteBookmark={handleRemoveBookmark}
          collections={bookmarkCollections}
          createCollection={createCollection}
          onCommentClick={toggleCommentsDrawer}
          commentsCount={getComments(post.integrationId).length}
          likesCount={getLikeData(post.integrationId).count}
          isLiked={getLikeData(post.integrationId).isLiked}
          onLikeClick={handleToggleLike}
          onCopyPostUrl={handleCopyPostUrl}
        ></BringzzPostingCard>
        {renderCommentsDrawer()}
        </>
      }

      {/* Type 3: Product Post - Sponsored product showcase post */}
      {post && post.type === 3 &&
        <>
        <BringzzPostingCard
          uuid={post.integrationId || null}
          avatar={preview ? (user?.userPic || AvatarExample) : (post.user_pic || AvatarExample)}
          name={preview ? (user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.email || 'User') : `${post.user_first_name || ''} ${post.user_last_name || ''}`.trim()}
          sponsored={preview ? false : (post.sponsored === 1 || post.sponsored === '1')}
          header={
            <BringzzImage
              className='w-full h-48 object-cover'
              src={post.ogImage ? post.ogImage : post.images[0].content}
              alt='post image'
            ></BringzzImage>
          }
          title={post.headline || ''}
          description={post.description || ''}
          action={{
            label: t('actions.viewProducts'),
            icon: 'IconEbook',
            iconFolder: 'LineIcons',
            onClick: () => {
              // Navigate to pharmacy profile products tab or product listing
              if (post.pharmacyUuid) {
                navigate(`/pharmacy/profile/${post.pharmacyUuid}?tab=products`);
              } else {
                navigate(`/post/details/${post.integrationId}`);
              }
            }
          }}
          isPreview={preview}
          addBookmark={addBookmark}
          hasBookmark={bookmarkStatus === 'success'}
          deleteBookmark={handleRemoveBookmark}
          collections={bookmarkCollections}
          createCollection={createCollection}
          onCommentClick={toggleCommentsDrawer}
          commentsCount={getComments(post.integrationId).length}
          likesCount={getLikeData(post.integrationId).count}
          isLiked={getLikeData(post.integrationId).isLiked}
          onLikeClick={handleToggleLike}
          onCopyPostUrl={handleCopyPostUrl}
        ></BringzzPostingCard>
        {renderCommentsDrawer()}
        </>
      }
      
      <BookmarkCollectionDrawer
        isOpen={showBookmarkDrawer}
        onClose={() => setShowBookmarkDrawer(false)}
        collections={bookmarkCollections}
        onSelectCollection={(collectionId) => addBookmarkToCollectionNew(collectionId)}
        onCreateCollection={createCollection}
      />
    </div>
  );
};

export default PostingCardElement;